import {
    ActionIcon,
    Badge,
    Button,
    Group,
    Modal,
    Paper,
    Stack,
    Text,
    Textarea,
    Title,
} from '@mantine/core'
import {useForm} from '@mantine/form'
import {IconBan, IconEdit, IconPlus} from '@tabler/icons-react'
import {useState} from 'react'

import {useEditRights} from 'src/jobs/edit-rights-context'
import {formatJobDate} from 'src/jobs/job-schedule-format'
import type {JobDetail} from 'src/jobs/types'

import {LoadEntryFields} from './load-entry-fields'
import {
    emptyLoadEntryForm,
    formatLoadCount,
    type LoadEntryFormValues,
    loadEntryRecordedLabel,
    loadEntryToForm,
    loadEntryVoidedLabel,
    toLoadEntryInput,
    validateLoadEntry,
} from './load-entry-logic'
import type {LoadEntry} from './schemas'
import {useJobLoads, useSaveJobLoadEntry, useVoidJobLoadEntry} from './use-job-loads'

type JobLoadsSectionProps = {
    job: Pick<JobDetail, 'id' | 'startDate' | 'endDate' | 'status' | 'didNotHappenAt'>
    onChargesChanged: () => void
}

export function JobLoadsSection({job, onChargesChanged}: JobLoadsSectionProps) {
    const {canEdit, canDispatch} = useEditRights()
    const canChange = canEdit && canDispatch
    const canRecord = canChange && job.status !== 'canceled' && job.didNotHappenAt == null
    const {data: loads, error: loadError} = useJobLoads(job.id)
    const save = useSaveJobLoadEntry(job.id)
    const voidEntry = useVoidJobLoadEntry(job.id)
    const [editing, setEditing] = useState<{entryId: string | null} | null>(null)
    const [voiding, setVoiding] = useState<LoadEntry | null>(null)
    const [voidReason, setVoidReason] = useState('')
    const form = useForm<LoadEntryFormValues>({
        initialValues: emptyLoadEntryForm(job, null),
        validate: (values) => validateLoadEntry(values),
        onValuesChange: () => {
            if (save.error) {
                save.reset()
            }
        },
    })

    if (loadError) {
        return (
            <Paper p="md" withBorder>
                <Text c="red" size="sm">
                    Failed to load job loads: {loadError.message}
                </Text>
            </Paper>
        )
    }
    if (!loads?.billedPerLoad) {
        return null
    }

    function openLoadEntryForm(entry: LoadEntry | null) {
        form.setValues(
            entry
                ? loadEntryToForm(entry, loads?.drivers)
                : emptyLoadEntryForm(job, loads?.drivers[0]?.assignmentId ?? null),
        )
        form.clearErrors()
        save.reset()
        setEditing({entryId: entry?.id ?? null})
    }

    async function handleSave(values: LoadEntryFormValues) {
        if (editing == null) {
            return
        }

        await save.mutateAsync({entryId: editing.entryId, input: toLoadEntryInput(values)})
        setEditing(null)
        onChargesChanged()
    }

    function openVoid(entry: LoadEntry) {
        voidEntry.reset()
        setVoidReason('')
        setVoiding(entry)
    }

    async function handleVoid(entryId: string) {
        await voidEntry.mutateAsync({entryId, reason: voidReason})
        setVoiding(null)
        onChargesChanged()
    }

    return (
        <Paper p="md" withBorder data-testid="job-loads-section">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Group gap="sm">
                        <Title order={3}>Loads hauled: {loads.totalLoads}</Title>
                        <Badge variant="light">Billed per load</Badge>
                    </Group>
                    {canRecord ? (
                        <Button
                            leftSection={<IconPlus size={16} />}
                            size="compact-sm"
                            variant="light"
                            onClick={() => openLoadEntryForm(null)}
                        >
                            Record loads
                        </Button>
                    ) : null}
                </Group>

                {loads.entries.length === 0 ? (
                    <Text c="dimmed" size="sm">
                        No loads recorded yet. The job cannot be completed until they are.
                    </Text>
                ) : (
                    loads.entries.map((entry) => (
                        <Group
                            key={entry.id}
                            justify="space-between"
                            align="flex-start"
                            wrap="nowrap"
                        >
                            <Stack gap={2}>
                                <Text
                                    size="sm"
                                    fw={500}
                                    td={entry.voidedAt ? 'line-through' : undefined}
                                    c={entry.voidedAt ? 'dimmed' : undefined}
                                >
                                    {formatJobDate(entry.loadDate)} ·{' '}
                                    {entry.workerName ?? 'Job total'} ·{' '}
                                    {formatLoadCount(entry.loadCount)}
                                    {entry.ticketNumber ? ` · Ticket ${entry.ticketNumber}` : ''}
                                </Text>
                                <Text size="xs" c="dimmed">
                                    {loadEntryRecordedLabel(entry)}
                                </Text>
                                {entry.voidedAt ? (
                                    <Text size="xs" c="dimmed">
                                        {loadEntryVoidedLabel(entry)}
                                    </Text>
                                ) : null}
                            </Stack>
                            {canChange && entry.voidedAt == null ? (
                                <Group gap="xs" wrap="nowrap">
                                    {canRecord ? (
                                        <ActionIcon
                                            variant="subtle"
                                            aria-label={`Edit loads for ${formatJobDate(entry.loadDate)}`}
                                            onClick={() => openLoadEntryForm(entry)}
                                        >
                                            <IconEdit size={16} />
                                        </ActionIcon>
                                    ) : null}
                                    <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        aria-label={`Void loads for ${formatJobDate(entry.loadDate)}`}
                                        onClick={() => openVoid(entry)}
                                    >
                                        <IconBan size={16} />
                                    </ActionIcon>
                                </Group>
                            ) : null}
                        </Group>
                    ))
                )}
            </Stack>

            <Modal
                opened={editing != null}
                onClose={() => setEditing(null)}
                title={editing?.entryId ? 'Edit loads' : 'Record loads'}
            >
                <form onSubmit={form.onSubmit((values) => void handleSave(values).catch(() => {}))}>
                    <Stack gap="md">
                        <LoadEntryFields form={form} drivers={loads.drivers} allowNoDriver />
                        {save.error ? (
                            <Text c="red" size="sm">
                                {save.error.message}
                            </Text>
                        ) : null}
                        <Group justify="flex-end">
                            <Button
                                variant="default"
                                onClick={() => setEditing(null)}
                                disabled={save.isPending}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" loading={save.isPending}>
                                Save
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </Modal>

            <Modal opened={voiding != null} onClose={() => setVoiding(null)} title="Void loads">
                {voiding ? (
                    <Stack gap="md">
                        <Text size="sm">
                            Void {formatLoadCount(voiding.loadCount)} on{' '}
                            {formatJobDate(voiding.loadDate)}? The entry stays listed, crossed out,
                            and its charge comes off billing.
                        </Text>
                        <Textarea
                            label="Reason"
                            placeholder="Optional"
                            value={voidReason}
                            onChange={(event) => setVoidReason(event.currentTarget.value)}
                            autosize
                            minRows={2}
                        />
                        {voidEntry.error ? (
                            <Text c="red" size="sm">
                                {voidEntry.error.message}
                            </Text>
                        ) : null}
                        <Group justify="flex-end">
                            <Button
                                variant="default"
                                onClick={() => setVoiding(null)}
                                disabled={voidEntry.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="red"
                                loading={voidEntry.isPending}
                                onClick={() => void handleVoid(voiding.id).catch(() => {})}
                            >
                                Void
                            </Button>
                        </Group>
                    </Stack>
                ) : null}
            </Modal>
        </Paper>
    )
}
